import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from "@angular/core";
import {
  Aangifte,
  BokkzIbDataService,
  IncomeTaxEntity,
  Onderneming,
  Ondernemingsvorm,
  Zeescheepvaart
} from '@snelstart-ventures/fiscaal/domain-logic';
import {FormArray, FormControl, FormGroup} from "@angular/forms";
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {filter, Observable, shareReplay, take} from "rxjs";
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiDialogModule} from '@snelstart/ui-dialog';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

type OndernemingFormGroup = FormGroup<{
    rsin: FormControl<string | null>;
    invullenOpNiveauSamenwerkingsverband: FormControl<boolean | null>;
    invullenOpPersoonlijkNiveau: FormControl<boolean | null>;
    invullenOpJaaropgaveCV: FormControl<boolean | null>;
    zeescheepvaart: FormControl<Zeescheepvaart[] | null>;
    startOnderneming: FormControl<Date | null>;
    datumStaking: FormControl<Date | null>;
    medegerechtigdeOfSchuldeiser: FormControl<boolean | null>;
    winstUitHoofdeVanSchuldvordering: FormControl<boolean | null>;
    agrarischeActiviteiten: FormControl<boolean | null>;
    waarderingsstelselGewijzigd: FormControl<boolean | null>;
    willekeurigAfgeschreven: FormControl<boolean | null>;
    schadeloosstellingBuitenWinstOm: FormControl<boolean | null>;
    staking: FormGroup<{
        stakingDoorOverdrachtOnderneming: FormControl<boolean | null>;
        overdrachtOnroerendeZakenNaarPrive: FormControl<boolean | null>;
        overdrachtOverigeBestanddelenNaarPrive: FormControl<boolean | null>;
        overdrachtNaarBV: FormControl<boolean | null>;
        doorschuivenGeruisloosInbrengInBV: FormControl<boolean | null>;
        doorschuivenGeruisloosNaarMedeOndernemer: FormControl<boolean | null>;
        doorschuivenGeruisloosDoorOverlijden: FormControl<boolean | null>;
    }>;
}>;

@Component({
  selector: 'snelstart-venture-fiscaal-feature-vragen-vooraf-ond',
  templateUrl: './vragen-vooraf-ond.component.html',
  styleUrls: ['./vragen-vooraf-ond.component.scss'],
  imports: [
    SharedUiDetailsModule,
    SharedUiFormControlsModule,
    SharedUiButtonModule,
    SharedUiDialogModule,
    CommonModule
  ]
})
export class VragenVoorafOndComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzIbDataService);
  private destroyRef = inject(DestroyRef);
  public isPartner = false;
  public index = 0;
  public indexOnderneming = 0;

  readonly incomeTaxEntity$: Observable<IncomeTaxEntity> =
    this.dataService.incomeTaxEntity$.pipe(
      filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  readonly indexOnderneming$: Observable<number> =
    this.dataService.indexOnderneming$.pipe(
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  public optionsJaNee: SelectFormControlItem<boolean>[] = [
    {value: false, label: "Nee"},
    {value: true, label: "Ja"},
  ]

  public optionsJaNeeAgra: SelectFormControlItem<boolean>[] = [
    {value: false, label: "Nee"},
    {value: true, label: "Ja (nog niet beschikbaar)", disabled: true},
  ]

  public ondernemingSelector: SelectFormControlItem<string>[] = []

  public formGroup = new FormGroup({
    ondernemingen: new FormArray<OndernemingFormGroup>([]),
  });

  public formGroupSelector= new FormGroup({
    selector: new FormControl<string>('0'),
  });

  ngOnInit(): void {
    this.indexOnderneming$.subscribe((index) => {
      this.indexOnderneming = index;
      this.formGroupSelector.patchValue({selector: index.toString()}, {emitEvent: false});
    });
    this.dataService.partnerAangifte$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(partner => {
      this.isPartner = partner;
      this.incomeTaxEntity$.pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(entity => {
        if (partner) {
          this.handleSwitch(entity, true);
        } else {
          this.handleSwitch(entity, false);
        }
      });
    });
    this.formGroup.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.saveForm();
    });
  }

  ngOnDestroy(): void {
    // rely on saveFrom in component
  }

  private handleSwitch(entity: IncomeTaxEntity, isPartner: boolean): void {
    const details = isPartner ? entity.aangifte?.partnerAangifte : entity.aangifte;
    if (details) {
      this.resetAndPatchForms(details);
    }
  }

  private resetAndPatchForms(details: any): void {
    this.formGroup.reset({}, {emitEvent: false}); // Prevent reset from emitting events
    this.ondernemingSelector = [];
    this.updateForm(details.box1?.winstUitOnderneming?.ondernemingen || []);
    this.saveForm();
  }

  get ondernemingen(): FormArray<OndernemingFormGroup> {
    return this.formGroup.controls.ondernemingen;
  }

  createFormGroup(item?: Onderneming): FormGroup {
    // Initialize all fields from Onderneming, using item values or sensible defaults
    return new FormGroup({
        naam: new FormControl<string | null>(item?.naam ?? null),
        werkzaamheden: new FormControl<string | null>(item?.werkzaamheden ?? null),
        administratieId: new FormControl<string | null>(item?.administratieId ?? null),
        ondernemingsvorm: new FormControl<any>(item?.ondernemingsvorm ?? null),
        rsin: new FormControl<any>(item?.rsin ?? null),
        landCode: new FormControl<string | null>(item?.landCode ?? null),
        invullenOpPersoonlijkNiveau: new FormControl<boolean | null>(item?.invullenOpPersoonlijkNiveau ?? null),
        invullenOpNiveauSamenwerkingsverband: new FormControl<boolean | null>(item?.invullenOpNiveauSamenwerkingsverband ?? true),
        invullenOpJaaropgaveCV: new FormControl<boolean | null>(item?.invullenOpJaaropgaveCV ?? false),
        zeescheepvaart: new FormControl<Zeescheepvaart[] | null>(item?.zeescheepvaart ?? null),
        startOnderneming: new FormControl<Date | null>(item?.startOnderneming ?? null),
        datumStaking: new FormControl<Date | null>(item?.datumStaking ?? null),
        medegerechtigdeOfSchuldeiser: new FormControl<boolean | null>(item?.medegerechtigdeOfSchuldeiser ?? false),
        winstUitHoofdeVanSchuldvordering: new FormControl<boolean | null>(item?.winstUitHoofdeVanSchuldvordering ?? false),
        agrarischeActiviteiten: new FormControl<boolean | null>(item?.agrarischeActiviteiten ?? false),
        waarderingsstelselGewijzigd: new FormControl<boolean | null>(item?.waarderingsstelselGewijzigd ?? false),
        willekeurigAfgeschreven: new FormControl<boolean | null>(item?.willekeurigAfgeschreven ?? false),
        schadeloosstellingBuitenWinstOm: new FormControl<boolean | null>(item?.schadeloosstellingBuitenWinstOm ?? false),
        staking: new FormGroup({
            stakingDoorOverdrachtOnderneming: new FormControl<boolean | null>(item?.staking?.stakingDoorOverdrachtOnderneming ?? false),
            overdrachtOnroerendeZakenNaarPrive: new FormControl<boolean | null>(item?.staking?.overdrachtOnroerendeZakenNaarPrive ?? false),
            overdrachtOverigeBestanddelenNaarPrive: new FormControl<boolean | null>(
                item?.staking?.overdrachtOverigeBestanddelenNaarPrive ?? false,
            ),
            overdrachtNaarBV: new FormControl<boolean | null>(item?.staking?.overdrachtNaarBV ?? false),
            doorschuivenGeruisloosInbrengInBV: new FormControl<boolean | null>(item?.staking?.doorschuivenGeruisloosInbrengInBV ?? false),
            doorschuivenGeruisloosNaarMedeOndernemer: new FormControl<boolean | null>(
                item?.staking?.doorschuivenGeruisloosNaarMedeOndernemer ?? false,
            ),
            doorschuivenGeruisloosDoorOverlijden: new FormControl<boolean | null>(
                item?.staking?.doorschuivenGeruisloosDoorOverlijden ?? false,
            ),
        }),
    });
  }

  private updateForm(ondernemingen: Onderneming[]): void {
    let lijst = ondernemingen || [];
    const formArray = this.ondernemingen;

    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1, {emitEvent: false});
    }

    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        const formGroup = formArray.at(i) as FormGroup;
        formGroup.patchValue(lijst[i], { emitEvent: false });
      } else {
        const formGroup = this.createFormGroup(lijst[i]);
        formArray.push(formGroup, { emitEvent: false });
      }
    }
  }

  saveForm(): void {
    this.formGroup.updateValueAndValidity({emitEvent: false});
    const formOndernemingen = this.formGroup.getRawValue().ondernemingen;
    const idx = this.indexOnderneming;
    this.dataService.patchIncomeTaxEntity((currentEntity) => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }

      const update = (aangifte: Aangifte) => {
        const existingOndernemingen = aangifte.box1?.winstUitOnderneming?.ondernemingen || [];
        const updatedOndernemingen = existingOndernemingen.map((onderneming, i) => {
          if (i !== idx) return onderneming;
          const formOnd = formOndernemingen[idx] as Partial<Onderneming>;
          const updatedOnderneming: Partial<Onderneming> = {
            ...onderneming,
            ...formOnd,
          };
          if (formOnd.datumStaking == null) {
            delete updatedOnderneming.wuoStakingswinsten;
            delete updatedOnderneming.datumStaking;
            delete updatedOnderneming.staking;
          }
          return updatedOnderneming as Onderneming;
        });
        return {
          ...aangifte,
          box1: {
            ...aangifte.box1,
            winstUitOnderneming: {
              ...aangifte.box1?.winstUitOnderneming,
              ondernemingen: updatedOndernemingen,
            },
          },
        };
      };

      const updatedAangifte = this.isPartner ? {
        ...currentEntity.aangifte,
        partnerAangifte: update(currentEntity.aangifte?.partnerAangifte!),
      } : update(currentEntity.aangifte);

      return {
        ...currentEntity,
        aangifte: updatedAangifte,
      } as IncomeTaxEntity;
    });
  }

  protected readonly Ondernemingsvorm = Ondernemingsvorm;
}
